import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import artworkSticker from 'src/assets/images/landing/artwork-sticker.png';
import { track, Events } from 'src/utils/analytics';

const SHADOW = '4px 4px 0px #1e1e1e';

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative sticker */}
      <Box
        component="img"
        src={artworkSticker}
        alt=""
        sx={{
          position: 'absolute',
          top: { xs: 20, md: 40 },
          right: { xs: -10, md: 60 },
          width: { xs: 40, md: 60 },
          opacity: 0.15,
          transform: 'rotate(-15deg)',
          display: { xs: 'none', md: 'block' },
        }}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 6, md: 8 },
            alignItems: 'center',
          }}
        >
          {/* Left: copy */}
          <Box>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontSize: { xs: '2.25rem', md: '3.25rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                mb: 3,
                color: '#2C2C2C',
                letterSpacing: '-0.02em',
              }}
            >
              Where Students Become Writers{' '}
              <Box
                component="span"
                sx={{
                  color: '#2C2C2C',
                  textDecoration: 'underline',
                  textDecorationColor: '#FFB300',
                  textUnderlineOffset: '6px',
                  textDecorationThickness: '4px',
                }}
              >
                and Journalists
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato, Inter, sans-serif',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.7,
                color: '#555',
                mb: 4,
                maxWidth: 520,
              }}
            >
              The writing layer for every curriculum — now with classroom newspapers.
              Turn any lesson into structured writing that makes student thinking visible.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <MuiButton
                variant="contained"
                onClick={() => { track(Events.ClickedCTA, { location: 'hero', label: 'Get Started Free', destination: '/register' }); navigate('/register'); }}
                sx={{
                  backgroundColor: '#2C2C2C',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: '6px',
                  border: '2px solid #1e1e1e',
                  boxShadow: SHADOW,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#1e1e1e',
                    boxShadow: '2px 2px 0px #1e1e1e',
                    transform: 'translate(2px, 2px)',
                  },
                  transition: 'all 100ms ease-in-out',
                }}
              >
                Get Started Free
              </MuiButton>
              <MuiButton
                variant="outlined"
                onClick={() => {
                  track(Events.ClickedCTA, { location: 'hero', label: 'See How It Works', destination: '#how-it-works' });
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  borderColor: '#2C2C2C',
                  border: '2px solid #2C2C2C',
                  color: '#2C2C2C',
                  fontFamily: 'Montserrat, Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: SHADOW,
                  px: 4,
                  py: 1.5,
                  backgroundColor: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                    borderColor: '#2C2C2C',
                    boxShadow: '2px 2px 0px #1e1e1e',
                    transform: 'translate(2px, 2px)',
                  },
                  transition: 'all 100ms ease-in-out',
                }}
              >
                See How It Works
              </MuiButton>
            </Box>
          </Box>

          {/* Right: newspaper card */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Box
              sx={{
                backgroundColor: '#faf7f0',
                borderRadius: '8px',
                p: 4,
                border: '2px solid #1e1e1e',
                boxShadow: '6px 6px 0px #1e1e1e',
                color: '#1a1a1a',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  borderBottom: '3px double #1a1a1a',
                  pb: 1.5,
                  mb: 2,
                }}
              >
                The Classroom Times
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontSize: '0.7rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  color: '#666',
                  mb: 2,
                }}
              >
                Spring Edition &bull; Written by Students
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {[
                  { emoji: '📰', title: 'School Garden Opens', tag: 'News Story' },
                  { emoji: '💬', title: 'Why Recess Matters', tag: 'Opinion' },
                  { emoji: '📚', title: 'Dog Man Review', tag: 'Book Review' },
                  { emoji: '🍕', title: 'Cafeteria Pizza', tag: 'Restaurant Review' },
                ].map((story) => (
                  <Box
                    key={story.title}
                    sx={{
                      p: 1.5,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '4px',
                      border: '1.5px solid #1e1e1e',
                      boxShadow: '2px 2px 0px #1e1e1e',
                    }}
                  >
                    <Typography sx={{ fontSize: '1.2rem', mb: 0.5 }}>{story.emoji}</Typography>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                      {story.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {story.tag}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
